import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import type {
  InsightsLabV2Favorite,
  InsightsLabV2Report,
} from 'api/insightsLabV2'
import SearchInput from 'components/SearchInput'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useEffect, useMemo, useRef } from 'react'

const t = buildTranslate('insights_lab_v2')

const MAX_SEARCH_RESULTS = 8
// Favorites are shown first (see favoriteMatches below), but capping the
// combined list by slicing favorites+reports together let a popular query
// with 8+ matching favorites push every matching report — including an
// exact title match — out of the results entirely. Reserving half the cap
// for each source guarantees a matching report always has room to appear,
// while still favoring favorites when both sources have few matches.
const MAX_FAVORITE_RESULTS = Math.ceil(MAX_SEARCH_RESULTS / 2)

type SearchResult =
  | { kind: 'favorite'; favorite: InsightsLabV2Favorite }
  | { kind: 'report'; report: InsightsLabV2Report }

type Props = {
  reports: InsightsLabV2Report[]
  favorites: InsightsLabV2Favorite[]
  searchTerm: string
  onSearchTermChange: (term: string) => void
  onOpenReport: (report: InsightsLabV2Report) => void
  onOpenFavorite: (favorite: InsightsLabV2Favorite) => void
  onOpenPicker: () => void
}

// The workspace's top search bar: a text box with a live results dropdown over
// the catalog, plus the "See all reports" button that opens the full picker
// modal. `searchTerm` is controlled by the parent so opening a report from any
// source (this dropdown, a favorite star, the picker modal) can clear it.
const WorkspaceSearch = ({
  reports,
  favorites,
  searchTerm,
  onSearchTermChange,
  onOpenReport,
  onOpenFavorite,
  onOpenPicker,
}: Props): JSX.Element => {
  const [css] = useStyletron()
  // Wraps the search box + its results dropdown so an outside click can dismiss
  // the dropdown (it otherwise lingers as a zIndex-20 overlay over the tab
  // content until the box is manually cleared).
  const searchContainerRef = useRef<HTMLDivElement>(null)

  // Favorites are surfaced ahead of catalog reports (a saved favorite is what
  // the user is most likely searching for). They already arrive from the API
  // most-recently-created first, so this only filters — it never reorders.
  const favoriteMatches = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()
    if (!query) return []
    return favorites.filter(
      favorite =>
        favorite.name.toLowerCase().includes(query) ||
        (favorite.reportTitle ?? '').toLowerCase().includes(query)
    )
  }, [favorites, searchTerm])

  // Excludes reports already surfaced via favoriteMatches -- a favorited
  // report whose title matches the query would otherwise appear twice (once
  // opening the favorite, once opening the report itself). Only excludes for
  // an unfiltered favorite (no saved filterState): a filtered favorite opens
  // a genuinely different state than the plain report, so it isn't a true
  // duplicate and both should stay listed.
  const reportMatches = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()
    if (!query) return []
    const unfilteredFavoriteReportIds = new Set(
      favoriteMatches
        .filter(favorite => Object.keys(favorite.filterState).length === 0)
        .map(favorite => favorite.lookerReportId)
    )
    return reports.filter(
      report =>
        !unfilteredFavoriteReportIds.has(report.id) &&
        (report.title.toLowerCase().includes(query) ||
          (report.categoryName ?? '').toLowerCase().includes(query))
    )
  }, [reports, searchTerm, favoriteMatches])

  const searchMatches: SearchResult[] = useMemo(() => {
    const cappedFavorites: SearchResult[] = favoriteMatches
      .slice(0, MAX_FAVORITE_RESULTS)
      .map(favorite => ({ kind: 'favorite', favorite }))
    // Whatever room favorites didn't use (including all of it, if favorites
    // matched fewer than their reserved share) goes to reports — so a
    // report match is never dropped just because favorites happened to fill
    // their own reserved slots.
    const remainingSlots = MAX_SEARCH_RESULTS - cappedFavorites.length
    const cappedReports: SearchResult[] = reportMatches
      .slice(0, remainingSlots)
      .map(report => ({ kind: 'report', report }))
    return [...cappedFavorites, ...cappedReports]
  }, [favoriteMatches, reportMatches])

  // Dismiss the search-results dropdown on an outside click so it doesn't linger
  // over the tab content. Only listen while results are showing.
  useEffect(() => {
    if (searchMatches.length === 0) return undefined
    const handleOutsideClick = (event: MouseEvent): void => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        onSearchTermChange('')
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [searchMatches.length, onSearchTermChange])

  return (
    <div className="d-flex align-items-start mb-m">
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        ref={searchContainerRef}
        className={`mr-s ${css({ position: 'relative', flexGrow: 1 })}`}
        // Escape clears the search (and so dismisses the results dropdown)
        // without forcing the user to manually empty the box. Bubbles up
        // from the input and the result buttons.
        onKeyDown={event => {
          if (event.key === 'Escape' && searchTerm) {
            onSearchTermChange('')
          }
        }}
      >
        <SearchInput
          className="w-100"
          value={searchTerm}
          onChange={onSearchTermChange}
          placeholder={t('search_placeholder')}
        />
        {searchMatches.length > 0 && (
          <div
            data-testid="insights-lab-v2-search-results"
            role="listbox"
            aria-label={t('search_placeholder')}
            className={css({
              position: 'absolute',
              top: '100%',
              left: '0',
              right: '0',
              zIndex: 20,
              backgroundColor: palette.white,
              border: `1px solid ${palette.grey20}`,
              borderRadius: '4px',
              maxHeight: '320px',
              overflowY: 'auto',
            })}
          >
            {searchMatches.map(result =>
              result.kind === 'favorite' ? (
                <Button
                  key={`favorite-${result.favorite.id}`}
                  type="button"
                  aria-label={t('open_report_named', {
                    title: result.favorite.name,
                  })}
                  // Bootstrap's `!important` utilities reliably beat uikit's
                  // own default inline-flex/kind-colored/padded BaseButton
                  // chrome (unlike the mixed border-shorthand-vs-longhand
                  // case in ReportButton.tsx/FavoriteRow.tsx, nothing here
                  // competes with a styletron rule for the same property, so
                  // there's no insertion-order fragility) — same classes the
                  // native `<button>` this replaces used, reproducing its
                  // exact block/full-width/left-aligned/borderless/
                  // transparent/24px-padded look including "no hover".
                  className="d-block w-100 text-left border-0 bg-transparent p-m"
                  // uikit's `Button` doesn't type `role`/`aria-selected` as
                  // top-level props (only `aria-*`/`data-*` attributes are
                  // exempt from its prop-type check; `role` is not), but
                  // `overrides.BaseButton.props` is spread directly onto the
                  // real underlying `<button>` — the option/list-item
                  // semantics this row needs are preserved without an `as`
                  // cast.
                  overrides={{
                    BaseButton: {
                      props: { role: 'option', 'aria-selected': false },
                    },
                  }}
                  onClick={() => onOpenFavorite(result.favorite)}
                >
                  <Text.BodyText3 bold tag="div">
                    {result.favorite.name}
                  </Text.BodyText3>
                  <Text.BodyText5 color="grey70">
                    {t('picker.favorites')}
                  </Text.BodyText5>
                </Button>
              ) : (
                <Button
                  key={`report-${result.report.id}`}
                  type="button"
                  aria-label={t('open_report_named', {
                    title: result.report.title,
                  })}
                  className="d-block w-100 text-left border-0 bg-transparent p-m"
                  overrides={{
                    BaseButton: {
                      props: { role: 'option', 'aria-selected': false },
                    },
                  }}
                  onClick={() => onOpenReport(result.report)}
                >
                  <Text.BodyText3 bold tag="div">
                    {result.report.title}
                  </Text.BodyText3>
                  {result.report.categoryName && (
                    <Text.BodyText5 color="grey70">
                      {result.report.categoryName}
                    </Text.BodyText5>
                  )}
                </Button>
              )
            )}
          </div>
        )}
      </div>
      <Button kind="secondary" onClick={onOpenPicker}>
        {t('see_all_reports')}
      </Button>
    </div>
  )
}

export default WorkspaceSearch
